'use client';

import { useState } from 'react';

interface ShareButtonProps {
  label: string;
  copiedLabel: string;
  /** `stack` = label above icon (article meta). `inline` = one line (category disclaimers). */
  layout?: 'stack' | 'inline';
  className?: string;
}

/**
 * Triggers the native Web Share API when available; falls back to copying the URL.
 */
export default function ShareButton({
  label,
  copiedLabel,
  layout = 'stack',
  className = '',
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: document.title,
      url: window.location.href,
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        // User cancelled the share sheet — do nothing
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      }
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — nothing more we can do
    }
  };

  const isInline = layout === 'inline';

  const icon = (
    <svg
      className={isInline ? 'w-3 h-3 shrink-0' : 'w-4 h-4 text-gray-800'}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );

  const text = (
    <span
      className={
        isInline
          ? undefined
          : 'text-[10px] font-normal uppercase tracking-wide'
      }
    >
      {copied ? copiedLabel : label}
    </span>
  );

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={label}
      className={
        isInline
          ? `inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded ${className}`
          : `inline-flex flex-col items-center gap-0.5 text-gray-500 hover:text-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded ${className}`
      }
    >
      {isInline ? (
        <>
          {icon}
          {text}
        </>
      ) : (
        <>
          {text}
          {icon}
        </>
      )}
    </button>
  );
}
